<script setup lang="ts">
import { computed } from 'vue';
import { EditableChip } from '@/design-system';
import OperatorDropdownItem from './OperatorDropdownItem.vue';
import type { ConditionOperatorType } from '@/types/HookConditionTypes';

type PropsType = {
    mode?: 'chip' | 'input';
    data?: string;
    items?: ConditionOperatorType[];
    loading?: boolean;
    autofocus?: boolean;
    autoOpenMenu?: boolean;
    autoCloseMenu?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    mode: 'chip',
    items: () => [],
    loading: false,
    autofocus: false,
    autoOpenMenu: false,
    autoCloseMenu: false,
});

const emit = defineEmits<{
    select: [operatorSymbol: string];
    click: [];
    focus: [isFocused: boolean];
    'update:menu': [isOpen: boolean];
}>();

const operatorObject = computed((): ConditionOperatorType | string | undefined => {
    if (!props.data) {
        return undefined;
    }

    if (props.mode === 'input') {
        const res = props.items.find(op => op.code === props.data);

        return res?.name || undefined;
    }

    return props.items.find(op => op.code === props.data) || undefined;
});

const displayText = computed(() => {
    return props.data || '';
});

const handleSelect = (operator: unknown) => {
    const selectedOperator = operator as ConditionOperatorType;
    emit('select', selectedOperator.code ?? selectedOperator.symbol);
};

const handleClick = () => {
    emit('click');
};

const handleMenuToggle = (value: boolean) => {
    emit('update:menu', value);
};
</script>

<template>
    <EditableChip
        :mode="props.mode"
        type="operator"
        :data="operatorObject"
        :display-text="displayText"
        :items="items"
        item-title="symbol"
        item-value="name"
        :loading="loading"
        :autofocus="autofocus"
        :auto-open-menu="autoOpenMenu"
        :auto-close-menu="autoCloseMenu"
        @select="handleSelect"
        @click="handleClick"
        @update:menu="handleMenuToggle"
    >
        <template #item="{ props: itemProps, item }">
            <OperatorDropdownItem
                :item-props="itemProps"
                :operator="item.raw as ConditionOperatorType"
            />
        </template>
    </EditableChip>
</template>
